import { useState } from "react";

import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import { ReviewStarPicker } from "./ReviewStarPicker";

/**
 * WorkedTogetherReviewSheet — reusable "worked together" review-capture
 * sheet (M11-A, plan 320/322) for the day-14 / passive-nudge / disconnect
 * review-ask variants.
 *
 * This is DELIBERATELY a separate surface from `ReviewCloseUpgradeModal`
 * (the post-"closed" congrats variant mounted on `CheckinCard`) and from
 * `DisconnectMarketplaceDialog`'s existing inline review step (tech-spec
 * §6/A5, already built/tested) — this component is NOT a refactor of
 * either; it is the shared sheet other M11 callers (day-14 nudge, passive
 * check-in, etc.) compose independently.
 *
 * Pure presentational piece: no data fetching, no wiring to the reviews
 * API. Callers post the result via the existing `submitMarketplaceReview`
 * client.
 *
 * Render order (first to last): headline -> helper line -> required 1-5
 * star picker -> optional free text -> foot line -> Post review -> Skip.
 *
 * Submit is disabled until a star is picked. Skip calls `onClose` only —
 * it never calls `onSubmit`.
 */

export interface WorkedTogetherReviewSubmission {
  stars: number;
  body: string;
}

export interface WorkedTogetherReviewSheetProps {
  /** Whether the sheet is open. Managed by the caller. */
  open: boolean;
  /** Agent's display name — drives the headline copy. */
  agentName: string;
  /** Weeks the buyer has been connected with this agent — drives the
   *  headline copy. */
  /** Omit when the caller has no real tenure value — the clause is dropped
   *  rather than rendered as "0 weeks". */
  weeksTogether?: number;
  /** Called on Submit with the buyer's answers. Never called on skip. */
  onSubmit: (result: WorkedTogetherReviewSubmission) => void;
  /** Called when the sheet is skipped/dismissed (Skip button, header
   *  close, escape, overlay click). Never calls `onSubmit`. */
  onClose: () => void;
}

/**
 * Headline copy — `{agentName}`/`{weeksTogether}` interpolated.
 *
 * The tenure clause is DROPPED when the caller has no real value. Passing a
 * placeholder 0 would render "You've been working together 0 weeks", which
 * is worse than saying nothing: it is visibly false to a buyer who has been
 * with their agent for months. Same discipline as the star average, which
 * renders nothing rather than a fabricated 0.0.
 */
export function WORKED_TOGETHER_REVIEW_HEADLINE(
  agentName: string,
  weeksTogether?: number,
): string {
  const lead = `How's it going with ${agentName}?`;
  if (weeksTogether == null || weeksTogether < 1) return lead;
  const unit = weeksTogether === 1 ? "week" : "weeks";
  return `${lead} You've been working together ${weeksTogether} ${unit}`;
}

// Reviews are final once posted (operator 2026-10-09). The one follow-up is
// the closing review, so promise that and never "update it anytime".
export const WORKED_TOGETHER_REVIEW_HELPER =
  "Your review helps other buyers choose. Once you close, you can add how it ended.";

export const WORKED_TOGETHER_REVIEW_BODY_PLACEHOLDER =
  "What's she been like to work with?";

export const WORKED_TOGETHER_REVIEW_FOOTLINE =
  'Shows as "Worked together" until you close a deal.';

export function WorkedTogetherReviewSheet({
  open,
  agentName,
  weeksTogether,
  onSubmit,
  onClose,
}: WorkedTogetherReviewSheetProps) {
  const [stars, setStars] = useState(0);
  const [body, setBody] = useState("");

  const handleSubmit = () => {
    if (stars === 0) return;
    onSubmit({ stars, body });
  };

  return (
    <Dialog
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      open={open}
    >
      <DialogContent title="Leave a review">
        <div
          className="space-y-4 py-2"
          data-testid="worked-together-review-body"
        >
          <p
            className="text-lg font-bold text-foreground"
            data-testid="worked-together-review-headline"
          >
            {WORKED_TOGETHER_REVIEW_HEADLINE(agentName, weeksTogether)}
          </p>
          <p
            className="text-sm text-muted-foreground"
            data-testid="worked-together-review-helper"
          >
            {WORKED_TOGETHER_REVIEW_HELPER}
          </p>

          <ReviewStarPicker
            onChange={setStars}
            testId="worked-together-review-stars"
            value={stars}
          />

          <textarea
            aria-label="Review text (optional)"
            className="w-full rounded-xl border border-border bg-muted/30 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            data-testid="worked-together-review-body-input"
            onChange={(e) => setBody(e.target.value)}
            placeholder={WORKED_TOGETHER_REVIEW_BODY_PLACEHOLDER}
            rows={3}
            value={body}
          />

          <p
            className="text-xs text-muted-foreground"
            data-testid="worked-together-review-footline"
          >
            {WORKED_TOGETHER_REVIEW_FOOTLINE}
          </p>

          <Button
            className="w-full"
            data-testid="worked-together-review-submit"
            disabled={stars === 0}
            onClick={handleSubmit}
          >
            Post review
          </Button>
          <Button
            className="w-full"
            data-testid="worked-together-review-skip"
            onClick={onClose}
            variant="ghost"
          >
            Skip
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
